import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  ArrowLeft, BarChart3, TrendingUp, TrendingDown, Clock, Users, FileText, Calendar, 
  Euro, CheckCircle, XCircle, AlertCircle, Target, Award, Activity, 
  Droplets, Thermometer, Wrench, MapPin, Building, PieChart, Star, Share2, Copy
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function Statistics() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');

  useEffect(() => {
    fetchStats();
  }, [period]);

  const fetchStats = async () => {
    try {
      const [statsRes, interventionsRes, usersRes, missionsRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/interventions`),
        axios.get(`${API}/users/technicians`).catch(() => ({ data: [] })),
        axios.get(`${API}/missions`).catch(() => ({ data: [] }))
      ]);
      
      const interventions = interventionsRes.data || [];
      const missions = missionsRes.data || [];
      const technicians = usersRes.data || [];
      const now = new Date();
      
      // Filtrer par période
      const filtered = interventions.filter(i => {
        const date = new Date(i.created_at);
        if (period === 'week') {
          const weekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
          return date >= weekAgo;
        } else if (period === 'month') {
          return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
        } else if (period === 'quarter') {
          const quarter = Math.floor(now.getMonth() / 3);
          const dateQuarter = Math.floor(date.getMonth() / 3);
          return dateQuarter === quarter && date.getFullYear() === now.getFullYear();
        } else {
          return date.getFullYear() === now.getFullYear();
        }
      });

      // Stats franchises
      const franchiseCount = filtered.filter(i => i.franchise).length;
      const franchiseTotal = filtered
        .filter(i => i.franchise && i.franchise_montant)
        .reduce((acc, i) => acc + parseFloat(String(i.franchise_montant).replace(/[^0-9.,]/g, '').replace(',', '.') || 0), 0);
      const franchisePending = filtered.filter(i => i.franchise && !i.franchise_recuperee).length;

      // Temps moyen d'intervention
      const withTimes = filtered.filter(i => i.heure_debut && i.heure_fin);
      const avgTime = withTimes.length > 0 
        ? withTimes.reduce((acc, i) => {
            const start = new Date(i.heure_debut);
            const end = new Date(i.heure_fin);
            return acc + (end - start) / (1000 * 60);
          }, 0) / withTimes.length
        : 0;

      // Par technicien
      const byTechnician = {};
      const techPerformance = {};
      filtered.forEach(i => {
        const name = i.technician_name || 'Inconnu';
        const id = i.technician_id;
        if (!byTechnician[name]) {
          byTechnician[name] = { count: 0, validated: 0, pending: 0, avgTime: [], franchise: 0, notes: [] };
        }
        byTechnician[name].count++;
        if (i.status === 'validated') byTechnician[name].validated++;
        if (i.status === 'pending' || i.status === 'submitted') byTechnician[name].pending++;
        if (i.franchise) byTechnician[name].franchise++;
        if (i.satisfaction_note !== null && i.satisfaction_note !== undefined && !isNaN(Number(i.satisfaction_note))) {
          byTechnician[name].notes.push(Number(i.satisfaction_note));
        }
        if (i.heure_debut && i.heure_fin) {
          const duration = (new Date(i.heure_fin) - new Date(i.heure_debut)) / (1000 * 60);
          byTechnician[name].avgTime.push(duration);
        }
      });

      // Calculer moyenne temps par technicien
      Object.keys(byTechnician).forEach(name => {
        const times = byTechnician[name].avgTime;
        byTechnician[name].avgTimeValue = times.length > 0 
          ? Math.round(times.reduce((a, b) => a + b, 0) / times.length)
          : 0;
        const notes = byTechnician[name].notes;
        byTechnician[name].noteCount = notes.length;
        byTechnician[name].avgNote = notes.length > 0
          ? notes.reduce((a, b) => a + b, 0) / notes.length
          : null;
      });

      // Par mois
      const byMonth = Array(12).fill(0);
      const byMonthValidated = Array(12).fill(0);
      interventions.forEach(i => {
        const date = new Date(i.created_at);
        if (date.getFullYear() === now.getFullYear()) {
          byMonth[date.getMonth()]++;
          if (i.status === 'validated') byMonthValidated[date.getMonth()]++;
        }
      });

      // Par type d'intervention
      const byType = {};
      filtered.forEach(i => {
        (i.type_intervention || []).forEach(t => {
          byType[t] = (byType[t] || 0) + 1;
        });
      });

      // Par type de bâtiment
      const byBuilding = {};
      filtered.forEach(i => {
        const type = i.type_batiment || 'Non spécifié';
        byBuilding[type] = (byBuilding[type] || 0) + 1;
      });

      // Par donneur d'ordre
      const byDonneur = {};
      filtered.forEach(i => {
        const donneur = i.donneur_ordre_nom || i.donneur_ordre || 'Non spécifié';
        byDonneur[donneur] = (byDonneur[donneur] || 0) + 1;
      });

      // Tendance (comparaison avec période précédente)
      const previousPeriod = interventions.filter(i => {
        const date = new Date(i.created_at);
        if (period === 'week') {
          const twoWeeksAgo = new Date(now - 14 * 24 * 60 * 60 * 1000);
          const oneWeekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
          return date >= twoWeeksAgo && date < oneWeekAgo;
        } else if (period === 'month') {
          const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
          return date >= lastMonth && date < thisMonth;
        }
        return false;
      });
      
      const trend = previousPeriod.length > 0 
        ? Math.round(((filtered.length - previousPeriod.length) / previousPeriod.length) * 100)
        : 0;

      // Missions stats
      const missionsPending = missions.filter(m => m.status === 'pending').length;
      const missionsInProgress = missions.filter(m => m.status === 'in_progress').length;
      const missionsCompleted = missions.filter(m => m.status === 'completed').length;

      setStats({
        ...statsRes.data,
        periodCount: filtered.length,
        franchiseCount,
        franchiseTotal,
        franchisePending,
        avgTime: Math.round(avgTime),
        byTechnician,
        byMonth,
        byMonthValidated,
        byType,
        byBuilding,
        byDonneur,
        technicians: technicians.length,
        trend,
        missionsPending,
        missionsInProgress,
        missionsCompleted,
        totalMissions: missions.length,
        validatedCount: filtered.filter(i => i.status === 'validated').length,
        pendingCount: filtered.filter(i => i.status === 'pending' || i.status === 'submitted').length,
        rejectedCount: filtered.filter(i => i.status === 'rejected').length,
      });
    } catch (e) {
      console.error(e);
      toast.error('Erreur chargement statistiques');
    } finally {
      setLoading(false);
    }
  };

  const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
  const periodLabels = { week: 'cette semaine', month: 'ce mois', quarter: 'ce trimestre', year: 'cette année' };

  // Note client sur 5 étoiles (la note est stockée sur 10)
  const renderStars = (note10) => {
    const note5 = note10 / 2;
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map(i => (
          <Star
            key={i}
            className={`h-4 w-4 ${i <= Math.round(note5) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  const shareTechnicianRating = async (name, avgNote, count) => {
    const text = `⭐ Satisfaction client — ${name}\nNote moyenne : ${avgNote.toFixed(1)}/10 (sur ${count} intervention${count > 1 ? 's' : ''}, ${periodLabels[period]})\n— KARDIF Investigation`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `Note de ${name}`, text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success('Note copiée dans le presse-papier');
      }
    } catch (e) { /* partage annulé */ }
  };

  const shareAllRatings = async (ratings) => {
    const lines = ratings.map(r => `• ${r.name} : ${r.avgNote.toFixed(1)}/10 (${r.noteCount} avis)`);
    const text = `⭐ Satisfaction client par technicien — ${periodLabels[period]}\n\n${lines.join('\n')}\n\n— KARDIF Investigation`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Satisfaction client KARDIF', text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success('Récapitulatif copié dans le presse-papier');
      }
    } catch (e) { /* partage annulé */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F8F5F0] to-[#EDE8E0]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#8B7355] border-t-transparent mx-auto"></div>
          <p className="mt-4 text-[#8B7355] font-medium">Chargement des statistiques...</p>
        </div>
      </div>
    );
  }

  const maxMonth = Math.max(...stats.byMonth);

  const techRatings = Object.entries(stats.byTechnician)
    .filter(([, d]) => d.avgNote !== null && d.avgNote !== undefined)
    .map(([name, d]) => ({ name, avgNote: d.avgNote, noteCount: d.noteCount }))
    .sort((a, b) => b.avgNote - a.avgNote);
  const globalAvgNote = techRatings.length > 0
    ? techRatings.reduce((a, r) => a + r.avgNote * r.noteCount, 0) / techRatings.reduce((a, r) => a + r.noteCount, 0)
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F5F0] to-[#EDE8E0]">
      {/* Header */}
      <header className="bg-white border-b border-[#D4C5A9] shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => nav(-1)}>
              <ArrowLeft className="h-5 w-5 text-[#8B7355]" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-[#1A1A1A] flex items-center gap-2">
                <BarChart3 className="h-6 w-6 text-[#8B7355]" />
                Statistiques & Analyses
              </h1>
              <p className="text-sm text-[#6B7280]">Vue d'ensemble de l'activité KARDIF</p>
            </div>
          </div>
          
          {/* Sélecteur de période */}
          <div className="flex gap-2 bg-[#F8F5F0] rounded-xl p-1">
            {['week', 'month', 'quarter', 'year'].map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  period === p 
                    ? 'bg-[#8B7355] text-white shadow-md' 
                    : 'text-[#6B7280] hover:bg-white'
                }`}
              >
                {p === 'week' ? 'Semaine' : p === 'month' ? 'Mois' : p === 'quarter' ? 'Trimestre' : 'Année'}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        
        {/* KPIs principaux */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="bg-gradient-to-br from-[#8B7355] to-[#6B5744] text-white border-none shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold">{stats.periodCount}</p>
                  <p className="text-sm text-white/80">Interventions</p>
                </div>
                <FileText className="h-10 w-10 text-white/30" />
              </div>
              {stats.trend !== 0 && (
                <div className={`mt-2 flex items-center gap-1 text-sm ${stats.trend > 0 ? 'text-green-300' : 'text-red-300'}`}>
                  {stats.trend > 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                  {stats.trend > 0 ? '+' : ''}{stats.trend}% vs période préc.
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-none shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold">{stats.validatedCount}</p>
                  <p className="text-sm text-white/80">Validées</p>
                </div>
                <CheckCircle className="h-10 w-10 text-white/30" />
              </div>
              <div className="mt-2 text-sm text-white/80">
                {stats.periodCount > 0 ? Math.round((stats.validatedCount / stats.periodCount) * 100) : 0}% du total
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white border-none shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold">{stats.pendingCount}</p>
                  <p className="text-sm text-white/80">En attente</p>
                </div>
                <Clock className="h-10 w-10 text-white/30" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-none shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold">{stats.avgTime}<span className="text-lg">min</span></p>
                  <p className="text-sm text-white/80">Temps moyen</p>
                </div>
                <Activity className="h-10 w-10 text-white/30" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-none shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold">{stats.franchiseTotal.toFixed(0)}<span className="text-lg">€</span></p>
                  <p className="text-sm text-white/80">Franchises</p>
                </div>
                <Euro className="h-10 w-10 text-white/30" />
              </div>
              <div className="mt-2 text-sm text-white/80">
                {stats.franchisePending} en attente
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-none shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold">{stats.technicians}</p>
                  <p className="text-sm text-white/80">Techniciens</p>
                </div>
                <Users className="h-10 w-10 text-white/30" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Graphiques et analyses */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Évolution mensuelle */}
          <Card className="border-[#D4C5A9] shadow-md">
            <CardHeader className="bg-gradient-to-r from-[#8B7355] to-[#A08060] text-white rounded-t-lg">
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Évolution mensuelle {new Date().getFullYear()}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="flex items-end justify-between gap-2 h-48">
                {stats.byMonth.map((count, idx) => (
                  <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full flex flex-col items-center">
                      <div 
                        className="w-full bg-gradient-to-t from-[#8B7355] to-[#D4C5A9] rounded-t-md transition-all hover:from-[#7A6548]"
                        style={{ height: maxMonth > 0 ? `${(count / maxMonth) * 140}px` : '4px', minHeight: '4px' }}
                      />
                      {stats.byMonthValidated[idx] > 0 && (
                        <div 
                          className="w-full bg-green-500 rounded-b-md -mt-1"
                          style={{ height: maxMonth > 0 ? `${(stats.byMonthValidated[idx] / maxMonth) * 140 * 0.3}px` : '0px' }}
                        />
                      )}
                    </div>
                    <span className="text-xs text-[#6B7280]">{months[idx]}</span>
                    {count > 0 && <span className="text-xs font-bold text-[#8B7355]">{count}</span>}
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-center gap-4 mt-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-gradient-to-t from-[#8B7355] to-[#D4C5A9] rounded"></div>
                  <span className="text-[#6B7280]">Total</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-500 rounded"></div>
                  <span className="text-[#6B7280]">Validées</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Performance par technicien */}
          <Card className="border-[#D4C5A9] shadow-md">
            <CardHeader className="bg-gradient-to-r from-[#8B7355] to-[#A08060] text-white rounded-t-lg">
              <CardTitle className="text-base flex items-center gap-2">
                <Award className="h-5 w-5" />
                Performance par technicien
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-4 max-h-64 overflow-y-auto">
                {Object.entries(stats.byTechnician)
                  .sort((a, b) => b[1].count - a[1].count)
                  .map(([name, data], idx) => (
                  <div key={name} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                          idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-amber-700' : 'bg-[#8B7355]'
                        }`}>
                          {idx < 3 ? ['🥇', '🥈', '🥉'][idx] : name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-[#1A1A1A]">{name}</p>
                          <p className="text-xs text-[#6B7280]">
                            {data.avgTimeValue}min moy. • {data.franchise} franchises
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-[#8B7355]">{data.count}</p>
                        <p className="text-xs text-green-600">{data.validated} validées</p>
                      </div>
                    </div>
                    <div className="flex gap-1 h-2">
                      <div 
                        className="bg-green-500 rounded-l"
                        style={{ width: `${data.count > 0 ? (data.validated / data.count) * 100 : 0}%` }}
                      />
                      <div 
                        className="bg-amber-500"
                        style={{ width: `${data.count > 0 ? (data.pending / data.count) * 100 : 0}%` }}
                      />
                      <div 
                        className="bg-gray-300 rounded-r flex-1"
                      />
                    </div>
                  </div>
                ))}
                {Object.keys(stats.byTechnician).length === 0 && (
                  <p className="text-center text-[#6B7280] py-8">Aucune donnée disponible</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Répartition par type d'intervention */}
          <Card className="border-[#D4C5A9] shadow-md">
            <CardHeader className="bg-gradient-to-r from-[#8B7355] to-[#A08060] text-white rounded-t-lg">
              <CardTitle className="text-base flex items-center gap-2">
                <Wrench className="h-5 w-5" />
                Types d'intervention
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-3">
                {Object.entries(stats.byType)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 8)
                  .map(([type, count]) => {
                    const percentage = stats.periodCount > 0 ? Math.round((count / stats.periodCount) * 100) : 0;
                    return (
                      <div key={type} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-[#1A1A1A] font-medium">{type}</span>
                          <span className="text-[#8B7355] font-bold">{count} ({percentage}%)</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div 
                            className="h-full bg-gradient-to-r from-[#8B7355] to-[#D4C5A9] rounded-full transition-all"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                {Object.keys(stats.byType).length === 0 && (
                  <p className="text-center text-[#6B7280] py-4">Aucune donnée</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Répartition par donneur d'ordre */}
          <Card className="border-[#D4C5A9] shadow-md">
            <CardHeader className="bg-gradient-to-r from-[#8B7355] to-[#A08060] text-white rounded-t-lg">
              <CardTitle className="text-base flex items-center gap-2">
                <Building className="h-5 w-5" />
                Top donneurs d'ordre
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-3">
                {Object.entries(stats.byDonneur)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 8)
                  .map(([donneur, count], idx) => (
                    <div key={donneur} className="flex items-center justify-between p-2 rounded-lg hover:bg-[#F8F5F0]">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-[#8B7355]/10 flex items-center justify-center text-[#8B7355] font-bold">
                          {idx + 1}
                        </div>
                        <span className="text-[#1A1A1A] font-medium truncate max-w-[200px]">{donneur}</span>
                      </div>
                      <span className="text-[#8B7355] font-bold">{count}</span>
                    </div>
                  ))}
                {Object.keys(stats.byDonneur).length === 0 && (
                  <p className="text-center text-[#6B7280] py-4">Aucune donnée</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Satisfaction client par technicien */}
        <Card className="border-[#D4C5A9] shadow-md" data-testid="tech-ratings-card">
          <CardHeader className="bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-t-lg">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Star className="h-5 w-5 fill-white" />
                Satisfaction client par technicien
              </CardTitle>
              {techRatings.length > 0 && (
                <Button
                  size="sm"
                  variant="secondary"
                  className="bg-white/20 hover:bg-white/30 text-white border-none"
                  onClick={() => shareAllRatings(techRatings)}
                  data-testid="share-all-ratings-btn"
                >
                  <Share2 className="h-4 w-4 mr-1" />
                  Tout partager
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-4">
            {globalAvgNote !== null && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mb-5 p-4 bg-amber-50 rounded-xl border border-amber-200">
                <div className="flex items-center gap-3">
                  <div className="text-4xl font-bold text-amber-600">{globalAvgNote.toFixed(1)}<span className="text-lg text-amber-400">/10</span></div>
                  {renderStars(globalAvgNote)}
                </div>
                <div className="text-sm text-[#6B7280]">Note moyenne globale ({periodLabels[period]})</div>
              </div>
            )}
            <div className="space-y-3">
              {techRatings.map((r, idx) => (
                <div
                  key={r.name}
                  className="flex items-center justify-between p-3 rounded-lg border border-[#E5E0D8] hover:bg-[#F8F5F0] transition-colors"
                  data-testid={`tech-rating-row-${idx}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 ${
                      idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-amber-700' : 'bg-[#8B7355]'
                    }`}>
                      {r.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-[#1A1A1A] truncate">{r.name}</p>
                      <div className="flex items-center gap-2">
                        {renderStars(r.avgNote)}
                        <span className="text-xs text-[#6B7280]">{r.noteCount} avis</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-xl font-bold text-amber-600">{r.avgNote.toFixed(1)}<span className="text-sm text-amber-400">/10</span></span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-amber-300 text-amber-700 hover:bg-amber-50"
                      onClick={() => shareTechnicianRating(r.name, r.avgNote, r.noteCount)}
                      data-testid={`share-rating-btn-${idx}`}
                    >
                      <Share2 className="h-4 w-4 sm:mr-1" />
                      <span className="hidden sm:inline">Partager</span>
                    </Button>
                  </div>
                </div>
              ))}
              {techRatings.length === 0 && (
                <p className="text-center text-[#6B7280] py-8">
                  Aucune note client pour {periodLabels[period]}. Les notes sont renseignées à la fin d'une intervention.
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Ligne 3: Missions et bâtiments */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Missions */}
          <Card className="border-[#D4C5A9] shadow-md">
            <CardHeader className="bg-gradient-to-r from-[#8B7355] to-[#A08060] text-white rounded-t-lg py-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Target className="h-5 w-5" />
                État des missions
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-amber-600" />
                    <span className="font-medium text-amber-800">En attente</span>
                  </div>
                  <span className="text-2xl font-bold text-amber-600">{stats.missionsPending}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2">
                    <Activity className="h-5 w-5 text-blue-600" />
                    <span className="font-medium text-blue-800">En cours</span>
                  </div>
                  <span className="text-2xl font-bold text-blue-600">{stats.missionsInProgress}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="font-medium text-green-800">Terminées</span>
                  </div>
                  <span className="text-2xl font-bold text-green-600">{stats.missionsCompleted}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Types de bâtiments */}
          <Card className="border-[#D4C5A9] shadow-md md:col-span-2">
            <CardHeader className="bg-gradient-to-r from-[#8B7355] to-[#A08060] text-white rounded-t-lg py-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Building className="h-5 w-5" />
                Types de bâtiments
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(stats.byBuilding).map(([type, count]) => {
                  const percentage = stats.periodCount > 0 ? Math.round((count / stats.periodCount) * 100) : 0;
                  return (
                    <div key={type} className="text-center p-4 bg-[#F8F5F0] rounded-xl">
                      <div className="text-3xl font-bold text-[#8B7355]">{count}</div>
                      <div className="text-sm text-[#6B7280] font-medium mt-1">{type}</div>
                      <div className="text-xs text-[#9CA3AF]">{percentage}%</div>
                    </div>
                  );
                })}
                {Object.keys(stats.byBuilding).length === 0 && (
                  <p className="col-span-4 text-center text-[#6B7280] py-4">Aucune donnée</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

      </main>
    </div>
  );
}

export default Statistics;
